import { useCallback, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import { EditableCampaignBudgetCell } from '@/components/EditableCampaignBudgetCell';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { CampaignPerformanceDialog } from '@/components/performance/CampaignPerformanceDialog';
import { CampaignPerformanceGraphButton } from '@/components/performance/CampaignPerformanceGraphButton';
import {
  BulkCampaignBudgetConfirmModal,
  type BulkCampaignBudgetPreviewRow,
} from '@/components/BulkCampaignBudgetConfirmModal';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { useWorkspaceDemoActions } from '@/components/workspace/useWorkspaceDemoActions';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
  WorkspaceOrderedTotalsRow,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import {
  CAMPAIGN_COLUMN_IDS,
  CAMPAIGN_ENTITY_COLUMNS,
  defaultColumnIds,
  entityColumnsForDemo,
  workspaceColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import {
  WorkspaceCampaignPlacementBidsCell,
  WorkspaceCampaignPlacementBidsHeader,
  WorkspaceCampaignPlacementMetricCells,
  WorkspaceCampaignPlacementMetricHeaders,
} from '@/components/workspace/WorkspaceCampaignPlacementCells';
import { formatWorkspaceRangeSubtitle } from '@/components/workspace/WorkspaceAdProductMetricsTable';
import { AutoBiddingColumnCell } from '@/components/workspace/AutoBiddingSwitch';
import { WorkspaceAutoBiddingModal } from '@/components/workspace/WorkspaceAutoBiddingModal';
import { OutOfBudgetBoostSwitch } from '@/components/workspace/OutOfBudgetBoostSwitch';
import { WorkspaceOutOfBudgetBoostModal } from '@/components/workspace/WorkspaceOutOfBudgetBoostModal';
import {
  WorkspaceCampaignAudienceModal,
  WorkspaceCampaignBudgetModal,
  WorkspaceCampaignPlacementModal,
  WorkspaceCampaignPlacementProgressModal,
  WorkspaceStateChangeModal,
} from '@/components/workspace/WorkspaceBulkActionModals';
import {
  WORKSPACE_CAMPAIGNS_SECTION_ID,
  profileCampaignDetailPath,
} from '@/components/workspace/workspacePaths';
import { useServerPagination } from '@/hooks/useServerPagination';
import { useWorkspaceCampaignsPage } from '@/hooks/useWorkspaceEntityPages';
import { useWorkspaceCampaignPlacementJob } from '@/hooks/useWorkspaceCampaignPlacementJob';
import {
  emptyCrossFilter,
  toggleCampaignFilter,
  type EntityCrossFilter,
  type EntityGraph,
  type WorkspaceColumnFilters,
} from '@/utils/entityCrossFilter';
import { supportsAutoBidding } from '@/utils/autoBidding';
import {
  EMPTY_DERIVED_METRICS,
  campaignLookupKey,
  totalsFromMetricRows,
  WORKSPACE_METRIC_COLUMNS,
  type DerivedMetricTotals,
} from '@/utils/workspaceAdProductMetrics';
import type { PlacementDerivedMetrics, PlacementId } from '@/utils/workspacePlacementMetrics';
import { campaignAdProductToReportId } from '@/api/reportAdProducts';
import { campaignAdProductLabel, type CampaignAdProduct, type SpCampaignNormalized } from '@/types/spCampaigns';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import {
  exportWorkspaceTableCsv,
  fetchAllWorkspaceEntityRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';
import {
  applyBulkCampaignBudgetUpdates,
  computeAdjustedBudget,
  resolveEditableCampaignBudget,
  summarizeBulkBudgetResults,
  type CampaignBudgetAdjustMode,
} from '@/api/campaignBudgetUpdate';
import {
  applyWorkspaceCampaignStateChange,
  summarizeWorkspaceCampaignStateChange,
  supportedCampaignActionIds,
} from '@/api/workspaceBulkActions';
import {
  applyWorkspaceCampaignAudience,
  partitionAudienceCampaigns,
  summarizeWorkspaceCampaignAudience,
} from '@/api/workspaceAudienceApply';
import type { CampaignPlacementAdjustment } from '@/api/workspaceCampaignPlacementTypes';

function CampaignStateBadge({ state }: { state: string }) {
  const normalized = state.toUpperCase();
  const variant =
    normalized === 'ENABLED' ? 'success' : normalized === 'PAUSED' ? 'warning' : 'neutral';
  return <Badge variant={variant}>{normalized}</Badge>;
}

export function WorkspaceCampaignsSection({
  profileId,
  graph,
  columnFilters,
  onColumnFiltersChange,
  appliedFilter,
  pendingCampaignKeys,
  onChangePendingCampaignKeys,
  selection,
  showApplyFilter,
  onApplyFilter,
  onDiscardSelection,
  hasActiveCrossFilter,
  previousByCampaignKey,
  placementByCampaign,
  previousPlacementByCampaign,
  deferredLoading,
  showPlacementColumns = true,
  requireReportActivity = true,
  onCatalogRefresh,
  onRefresh,
  onOpenCampaignDetails,
  sectionId = WORKSPACE_CAMPAIGNS_SECTION_ID,
  showTitle = true,
  toolbarAction,
}: {
  profileId: string;
  graph: EntityGraph | null;
  columnFilters: Pick<
    WorkspaceColumnFilters,
    'campaignSearch' | 'campaignStatus' | 'campaignAdProduct'
  >;
  onColumnFiltersChange: (patch: Partial<WorkspaceColumnFilters>) => void;
  appliedFilter: EntityCrossFilter;
  pendingCampaignKeys: string[];
  onChangePendingCampaignKeys: (update: (keys: string[]) => string[]) => void;
  selection: { selectionCount: number; hasPendingChanges: boolean; canApply: boolean };
  showApplyFilter: boolean;
  onApplyFilter?: () => void;
  onDiscardSelection: () => void;
  hasActiveCrossFilter: boolean;
  previousByCampaignKey: Map<string, DerivedMetricTotals>;
  placementByCampaign: Map<string, Map<PlacementId, PlacementDerivedMetrics>>;
  previousPlacementByCampaign: Map<string, Map<PlacementId, PlacementDerivedMetrics>>;
  deferredLoading: boolean;
  /** Placement bid and placement metric columns. Hidden on the catalog campaign list. */
  showPlacementColumns?: boolean;
  /** False lists every catalog campaign, including those with no report rows. */
  requireReportActivity?: boolean;
  onCatalogRefresh: () => void;
  onRefresh: () => void;
  onOpenCampaignDetails?: (event: MouseEvent<HTMLAnchorElement>, to: string) => void;
  sectionId?: string;
  /** False when the page header already reads "Campaigns". */
  showTitle?: boolean;
  /** Extra control rendered after Columns, at the right end of the toolbar. */
  toolbarAction?: ReactNode;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isDemo = Boolean(user?.is_demo);
  const queryClient = useQueryClient();
  const { range: rangeState } = useAppDateRange();
  const compareEnabled = rangeState.compareEnabled;
  const rangeSubtitle = formatWorkspaceRangeSubtitle(
    rangeState.current.startDate,
    rangeState.current.endDate,
    rangeState.previous.startDate,
    rangeState.previous.endDate,
    compareEnabled
  );

  const [campaignBudgetModalOpen, setCampaignBudgetModalOpen] = useState(false);
  const [campaignBudgetPreviewRows, setCampaignBudgetPreviewRows] = useState<
    BulkCampaignBudgetPreviewRow[] | null
  >(null);
  const [campaignBudgetApplying, setCampaignBudgetApplying] = useState(false);
  const [campaignStatusModalOpen, setCampaignStatusModalOpen] = useState(false);
  const [campaignStatusApplying, setCampaignStatusApplying] = useState(false);
  const [campaignPlacementModalOpen, setCampaignPlacementModalOpen] = useState(false);
  const [campaignAudienceModalOpen, setCampaignAudienceModalOpen] = useState(false);
  const [campaignAudienceApplying, setCampaignAudienceApplying] = useState(false);
  const [autoBiddingCampaign, setAutoBiddingCampaign] = useState<{
    campaignId: string;
    campaignName: string;
    adProduct: string;
  } | null>(null);
  const [outOfBudgetBoostCampaign, setOutOfBudgetBoostCampaign] = useState<{
    scope: 'campaign';
    campaignId: string;
    name: string;
    adProduct: string;
  } | null>(null);
  const [performanceDialogCampaign, setPerformanceDialogCampaign] = useState<{
    campaignId: string;
    campaignName: string;
  } | null>(null);

  const campaignPlacementJob = useWorkspaceCampaignPlacementJob(profileId);
  const campaignSort = useTableSort({ defaultSortBy: 'name' });
  const campaignMetricFilters = useMetricFilters({
    tableId: 'campaigns',
    metrics: WORKSPACE_METRIC_COLUMNS,
  });
  const campaignEntityColumns = useMemo(
    () => entityColumnsForDemo(CAMPAIGN_ENTITY_COLUMNS, isDemo),
    [isDemo]
  );
  const campaignColumnIds = useMemo(
    () => (isDemo ? defaultColumnIds(campaignEntityColumns) : CAMPAIGN_COLUMN_IDS),
    [isDemo, campaignEntityColumns]
  );
  const campaignColumns = useColumnOrder(campaignColumnIds);
  const campaignFreeze = useWorkspaceFrozenLayout(
    campaignColumns.frozenColumnIds,
    campaignColumns.displayOrder.join('\0')
  );

  const campaignPageResetKey = [
    columnFilters.campaignSearch,
    columnFilters.campaignStatus,
    columnFilters.campaignAdProduct,
    JSON.stringify(appliedFilter),
    rangeState.current.startDate,
    rangeState.current.endDate,
    campaignSort.sortBy,
    campaignSort.sortDir,
    campaignMetricFilters.serialized,
  ].join('|');
  const campaignPager = useServerPagination(campaignPageResetKey);

  const campaignsPage = useWorkspaceCampaignsPage({
    profileId,
    startDate: rangeState.current.startDate,
    endDate: rangeState.current.endDate,
    page: campaignPager.page,
    pageSize: campaignPager.pageSize,
    search: columnFilters.campaignSearch,
    status: columnFilters.campaignStatus,
    adProduct: columnFilters.campaignAdProduct,
    appliedFilter,
    sortBy: campaignSort.sortBy,
    sortDir: campaignSort.sortDir,
    metricFilters: campaignMetricFilters.payload,
    requireActivity: requireReportActivity,
    enabled: Boolean(profileId),
  });

  const campaignsLoading = campaignsPage.isPending;
  const allCampaignsSelected =
    campaignsPage.rows.length > 0 &&
    campaignsPage.rows.every((row) => pendingCampaignKeys.includes(row.campaignKey));

  const demoCampaignOptions = useMemo(
    () =>
      (graph?.campaigns ?? []).map((campaign) => ({ id: campaign.campaignId, name: campaign.name })),
    [graph]
  );
  const { openDemoAction, demoModals } = useWorkspaceDemoActions({
    showToast,
    campaigns: demoCampaignOptions,
  });

  const selectedCampaigns = useMemo(
    () =>
      pendingCampaignKeys
        .map((key) => graph?.indices.campaignsByKey.get(key)?.raw ?? null)
        .filter((campaign): campaign is SpCampaignNormalized => campaign != null),
    [graph, pendingCampaignKeys]
  );
  const selectedAudienceCampaigns = useMemo(
    () => partitionAudienceCampaigns(selectedCampaigns),
    [selectedCampaigns]
  );

  const campaignCurrentTotals = totalsFromMetricRows(campaignsPage.rows.map((row) => row.metrics));
  const campaignPreviousTotals = totalsFromMetricRows(
    campaignsPage.rows.map((row) => {
      const reportKey = campaignLookupKey(
        campaignAdProductToReportId(row.adProduct as CampaignAdProduct),
        row.campaignId
      );
      return previousByCampaignKey.get(reportKey) ?? EMPTY_DERIVED_METRICS;
    })
  );

  const budgetByCampaignKey = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const campaign of graph?.campaigns ?? []) {
      map.set(
        campaign.campaignKey,
        typeof campaign.raw.budget === 'number' ? campaign.raw.budget : null
      );
    }
    return map;
  }, [graph]);

  const campaignNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const campaign of graph?.campaigns ?? []) {
      map.set(campaign.campaignId, campaign.name);
    }
    return map;
  }, [graph]);

  const invalidateCampaigns = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['workspace-entity-page', 'campaigns', profileId],
    });
  }, [profileId, queryClient]);

  const exportCampaigns = useCallback(async () => {
    try {
      const fetched = await fetchAllWorkspaceEntityRows({
        profileId,
        entity: 'campaigns',
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        columnFilters: {
          search: columnFilters.campaignSearch || undefined,
          status:
            columnFilters.campaignStatus !== 'ALL' ? columnFilters.campaignStatus : undefined,
          adProduct:
            columnFilters.campaignAdProduct !== 'ALL'
              ? columnFilters.campaignAdProduct
              : undefined,
        },
        appliedFilter: appliedFilter as unknown as Record<string, unknown>,
        sortBy: campaignSort.sortBy,
        sortDir: campaignSort.sortDir,
        metricFilters: campaignMetricFilters.payload,
        requireActivity: requireReportActivity,
      });
      const columns = workspaceExportColumns(campaignColumns.visibleIds, campaignEntityColumns);
      exportWorkspaceTableCsv({
        filenamePrefix: 'workspace_campaigns',
        startDate: rangeState.current.startDate,
        endDate: rangeState.current.endDate,
        columns,
        rows: fetched.rows,
        extras: {
          campaignNameById,
          budgetByCampaignKey,
        },
      });
      if (fetched.truncated) {
        showToast(
          `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
          'error'
        );
      } else {
        showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to export table', 'error');
    }
  }, [
    appliedFilter,
    budgetByCampaignKey,
    campaignColumns.visibleIds,
    campaignEntityColumns,
    campaignMetricFilters.payload,
    campaignSort.sortBy,
    campaignSort.sortDir,
    campaignNameById,
    columnFilters.campaignAdProduct,
    columnFilters.campaignSearch,
    columnFilters.campaignStatus,
    profileId,
    rangeState.current.endDate,
    rangeState.current.startDate,
    requireReportActivity,
    showToast,
  ]);

  const handleCampaignActionSelect = useCallback(
    (actionId: string) => {
      if (actionId === 'change_budget') {
        setCampaignBudgetModalOpen(true);
        return;
      }
      if (actionId === 'smart_budget') {
        if (selectedCampaigns.length !== 1) {
          showToast('Select exactly one campaign for Smart Budget', 'error');
          return;
        }
        const campaignId = selectedCampaigns[0].campaignId;
        navigate(
          `/accounts/${encodeURIComponent(profileId)}/campaigns/automations?tab=smart-budget&campaignId=${encodeURIComponent(campaignId)}`
        );
        return;
      }
      if (actionId === 'change_status') {
        setCampaignStatusModalOpen(true);
        return;
      }
      if (actionId === 'change_placement') {
        if (selection.selectionCount <= 0) {
          showToast('Select at least one campaign', 'error');
          return;
        }
        setCampaignPlacementModalOpen(true);
        return;
      }
      if (actionId === 'add_audience') {
        if (selection.selectionCount <= 0) {
          showToast('Select at least one campaign', 'error');
          return;
        }
        if (selectedAudienceCampaigns.eligible.length === 0) {
          showToast(
            'Add Audience applies to Sponsored Products and Sponsored Brands campaigns only',
            'error'
          );
          return;
        }
        setCampaignAudienceModalOpen(true);
        return;
      }
      if (isDemo) {
        openDemoAction(actionId, {
          entityLabel: 'campaign',
          selectionCount: selection.selectionCount,
        });
      }
    },
    [
      isDemo,
      navigate,
      openDemoAction,
      profileId,
      selectedAudienceCampaigns.eligible.length,
      selectedCampaigns,
      selection.selectionCount,
      showToast,
    ]
  );

  const handleOpenCampaignBudgetPreview = useCallback(
    (mode: CampaignBudgetAdjustMode, value: number) => {
      if (!Number.isFinite(value) || value <= 0) {
        showToast('Enter a valid adjustment value greater than 0', 'error');
        return;
      }
      if (mode === 'DECREASE_PERCENT' && value > 100) {
        showToast('Decrease percentage cannot exceed 100%', 'error');
        return;
      }
      if (!selectedCampaigns.length) {
        showToast('Select at least one campaign', 'error');
        return;
      }
      const rows: BulkCampaignBudgetPreviewRow[] = selectedCampaigns.map((campaign) => {
        const { budget, error } = computeAdjustedBudget(
          campaign.budget,
          mode,
          value,
          campaign.adProduct,
          campaign.budgetType
        );
        return {
          campaign,
          newBudget: budget ?? 0,
          error,
        };
      });
      setCampaignBudgetModalOpen(false);
      setCampaignBudgetPreviewRows(rows);
    },
    [selectedCampaigns, showToast]
  );

  const handleConfirmCampaignBudget = useCallback(async () => {
    if (!campaignBudgetPreviewRows) return;
    const updates = campaignBudgetPreviewRows
      .filter((row) => !row.error)
      .map((row) => ({
        campaignId: row.campaign.campaignId,
        adProduct: row.campaign.adProduct,
        budget: row.newBudget,
        budgetType: row.campaign.budgetType || 'DAILY',
        previousBudget: row.campaign.budget,
      }));
    if (!updates.length) {
      showToast('No valid campaign budget updates to apply', 'error');
      return;
    }
    setCampaignBudgetApplying(true);
    try {
      const results = await applyBulkCampaignBudgetUpdates(profileId, updates);
      const failures = results.filter((item) => item.status === 'error');
      showToast(summarizeBulkBudgetResults(results), failures.length ? 'error' : 'success');
      setCampaignBudgetPreviewRows(null);
      onDiscardSelection();
      invalidateCampaigns();
      onRefresh();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update campaign budgets',
        'error'
      );
    } finally {
      setCampaignBudgetApplying(false);
    }
  }, [
    campaignBudgetPreviewRows,
    invalidateCampaigns,
    onDiscardSelection,
    onRefresh,
    profileId,
    showToast,
  ]);

  const handleConfirmCampaignState = useCallback(
    async (state: 'ENABLED' | 'PAUSED') => {
      if (!selectedCampaigns.length) {
        showToast('Select at least one campaign', 'error');
        return;
      }
      setCampaignStatusApplying(true);
      try {
        const result = await applyWorkspaceCampaignStateChange(profileId, selectedCampaigns, state);
        showToast(
          summarizeWorkspaceCampaignStateChange(result.successCount, result.failureCount, state),
          result.failureCount ? 'error' : 'success'
        );
        setCampaignStatusModalOpen(false);
        onDiscardSelection();
        invalidateCampaigns();
        onRefresh();
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to update campaign status',
          'error'
        );
      } finally {
        setCampaignStatusApplying(false);
      }
    },
    [invalidateCampaigns, onDiscardSelection, onRefresh, profileId, selectedCampaigns, showToast]
  );

  const handleConfirmCampaignPlacement = useCallback(
    async (adjustments: CampaignPlacementAdjustment[]) => {
      if (!selectedCampaigns.length) {
        showToast('Select at least one campaign', 'error');
        return;
      }
      setCampaignPlacementModalOpen(false);
      try {
        const final = await campaignPlacementJob.start({
          adjustments,
          campaigns: selectedCampaigns.map((campaign) => ({
            campaignId: campaign.campaignId,
            adProduct: campaign.adProduct,
          })),
        });
        const failed = final.totals.failed ?? 0;
        const applied = final.totals.applied ?? 0;
        const skipped = final.totals.skipped ?? 0;
        if (final.status === 'failed') {
          showToast(final.applyProgress.error || 'Placement bidding job failed', 'error');
        } else if (failed > 0) {
          showToast(
            `Updated ${applied} campaign${applied === 1 ? '' : 's'}, ${failed} failed`,
            'error'
          );
        } else {
          const skipNote = skipped > 0 ? ` (${skipped} skipped)` : '';
          showToast(
            `Updated placement bidding for ${applied} campaign${applied === 1 ? '' : 's'}${skipNote}`,
            'success'
          );
          onDiscardSelection();
        }
        invalidateCampaigns();
        onRefresh();
      } catch (error) {
        const detail =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        showToast(
          typeof detail === 'string'
            ? detail
            : error instanceof Error
              ? error.message
              : 'Failed to apply placement bidding',
          'error'
        );
      }
    },
    [
      campaignPlacementJob,
      invalidateCampaigns,
      onDiscardSelection,
      onRefresh,
      selectedCampaigns,
      showToast,
    ]
  );

  const handleConfirmCampaignAudience = useCallback(
    async (audienceId: string, percentage: number) => {
      if (!selectedAudienceCampaigns.eligible.length) {
        showToast(
          'Add Audience applies to Sponsored Products and Sponsored Brands campaigns only',
          'error'
        );
        return;
      }
      setCampaignAudienceApplying(true);
      try {
        const result = await applyWorkspaceCampaignAudience(
          profileId,
          selectedCampaigns,
          audienceId,
          percentage
        );
        showToast(
          summarizeWorkspaceCampaignAudience(
            result.successCount,
            result.failureCount,
            result.skippedCount
          ),
          result.failureCount ? 'error' : 'success'
        );
        setCampaignAudienceModalOpen(false);
        if (result.successCount > 0) {
          onDiscardSelection();
        }
        invalidateCampaigns();
        onRefresh();
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Failed to apply audience', 'error');
      } finally {
        setCampaignAudienceApplying(false);
      }
    },
    [
      invalidateCampaigns,
      onDiscardSelection,
      onRefresh,
      profileId,
      selectedAudienceCampaigns.eligible.length,
      selectedCampaigns,
      showToast,
    ]
  );

  const openDetails = (event: MouseEvent<HTMLAnchorElement>, to: string) => {
    onOpenCampaignDetails?.(event, to);
  };

  return (
    <>
      <WorkspaceSection
        id={sectionId}
        title={showTitle ? 'Campaigns' : undefined}
        countLabel={`${campaignsPage.totalCount} total · ${rangeSubtitle}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceMetricFiltersPopover
              metrics={WORKSPACE_METRIC_COLUMNS}
              {...campaignMetricFilters}
            />
            <WorkspaceExportButton onExport={() => void exportCampaigns()} />
            <ReportColumnPicker
              columns={workspaceColumnPickerOptions(campaignEntityColumns)}
              selected={campaignColumns.visibleIds}
              onChange={campaignColumns.setVisibleIds}
              requiredColumnId="name"
              panelTitle="Visible columns"
            />
            {toolbarAction}
          </div>
        }
        toolbar={
          <div className="flex flex-wrap items-end gap-3">
            <FilterSearch
              id="workspace-campaign-search"
              label="Campaigns"
              value={columnFilters.campaignSearch}
              onChange={(value) => onColumnFiltersChange({ campaignSearch: value })}
            />
            <FilterSelect
              id="workspace-campaign-product"
              label="Ad product"
              value={columnFilters.campaignAdProduct}
              onChange={(value) =>
                onColumnFiltersChange({
                  campaignAdProduct: value as typeof columnFilters.campaignAdProduct,
                })
              }
            >
              <option value="ALL">All</option>
              <option value="SP">{campaignAdProductLabel('SP')}</option>
              <option value="SB">{campaignAdProductLabel('SB')}</option>
              <option value="SD">{campaignAdProductLabel('SD')}</option>
            </FilterSelect>
            <FilterSelect
              id="workspace-campaign-status"
              label="Status"
              value={columnFilters.campaignStatus}
              onChange={(value) =>
                onColumnFiltersChange({
                  campaignStatus: value as typeof columnFilters.campaignStatus,
                })
              }
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </FilterSelect>
            <WorkspaceTableSelectionActions
              entityLabel="campaign"
              selectionCount={selection.selectionCount}
              hasPendingChanges={selection.hasPendingChanges}
              canApply={selection.canApply}
              onApply={() => onApplyFilter?.()}
              onClearSelection={onDiscardSelection}
              disabled={campaignsLoading}
              actions={supportedCampaignActionIds(isDemo)}
              onActionSelect={handleCampaignActionSelect}
              showApplyFilter={showApplyFilter}
            />
          </div>
        }
      >
        <WorkspaceTableShell
          isEmpty={!campaignsPage.isPending && campaignsPage.rows.length === 0}
          isLoading={campaignsLoading}
          emptyMessage={
            hasActiveCrossFilter
              ? 'No campaigns match the current selection.'
              : 'No campaigns for this account.'
          }
          footer={
            <WorkspaceTablePagination
              page={campaignPager.page}
              pageSize={campaignPager.pageSize}
              total={campaignsPage.totalCount}
              onPageChange={campaignPager.setPage}
              onPageSizeChange={campaignPager.setPageSize}
              disabled={campaignsLoading}
            />
          }
        >
          <table ref={campaignFreeze.tableRef} className={workspaceTableClass.table}>
            <thead>
              <tr>
                <WorkspaceCheckboxCell isHeader>
                  <SelectionCheckbox
                    checked={allCampaignsSelected}
                    label="Select all visible campaigns"
                    disabled={campaignsLoading}
                    onChange={(checked) => {
                      onChangePendingCampaignKeys((keys) => {
                        if (checked) {
                          const next = new Set(keys);
                          for (const row of campaignsPage.rows) next.add(row.campaignKey);
                          return [...next];
                        }
                        const pageKeys = new Set(campaignsPage.rows.map((row) => row.campaignKey));
                        return keys.filter((key) => !pageKeys.has(key));
                      });
                    }}
                  />
                </WorkspaceCheckboxCell>
                <WorkspaceOrderedHeaders
                  order={campaignColumns.displayOrder}
                  entityColumns={campaignEntityColumns}
                  sortBy={campaignSort.sortBy}
                  sortDir={campaignSort.sortDir}
                  onSort={campaignSort.toggleSort}
                  onFreeze={campaignColumns.freezeColumn}
                  frozenColumnIds={campaignColumns.frozenColumnIds}
                  pinnedFrozenColumnId={campaignColumns.pinnedFrozenColumnId}
                  frozenLeftById={campaignFreeze.frozenLeftById}
                  onDragStartColumn={campaignColumns.startDrag}
                  onDragHoverColumn={campaignColumns.hoverReorder}
                  onDropColumn={campaignColumns.commitDrop}
                  onDragEndColumn={campaignColumns.endDrag}
                  activeDragColumnId={campaignColumns.activeDragColumnId}
                  hoverColumnId={campaignColumns.hoverColumnId}
                  hoverPosition={campaignColumns.hoverPosition}
                  hoverInsertIndex={campaignColumns.hoverInsertIndex}
                />
                {showPlacementColumns ? (
                  <>
                    <WorkspaceCampaignPlacementBidsHeader />
                    <WorkspaceCampaignPlacementMetricHeaders />
                  </>
                ) : null}
                <th className={workspaceTableClass.th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {campaignsPage.rows.map((row) => {
                const selected = pendingCampaignKeys.includes(row.campaignKey);
                const reportKey = campaignLookupKey(
                  campaignAdProductToReportId(row.adProduct as CampaignAdProduct),
                  row.campaignId
                );
                const campaignNode = graph?.indices.campaignsByKey.get(row.campaignKey);
                const detailsPath = profileCampaignDetailPath(
                  profileId,
                  row.campaignId,
                  row.adProduct as CampaignAdProduct
                );
                return (
                  <tr key={row.campaignKey} className={tableRowClass(selected)}>
                    <WorkspaceCheckboxCell>
                      <SelectionCheckbox
                        checked={selected}
                        label={`Select campaign ${row.name}`}
                        disabled={campaignsLoading}
                        onChange={(checked) =>
                          onChangePendingCampaignKeys((keys) => {
                            const next = toggleCampaignFilter(
                              { ...emptyCrossFilter(), campaignKeys: keys },
                              row.campaignKey,
                              checked
                            );
                            return next.campaignKeys;
                          })
                        }
                      />
                    </WorkspaceCheckboxCell>
                    <WorkspaceOrderedMetricCells
                      order={campaignColumns.displayOrder}
                      frozenColumnIds={campaignColumns.frozenColumnIds}
                      frozenLeftById={campaignFreeze.frozenLeftById}
                      metrics={row.metrics}
                      previousMetrics={previousByCampaignKey.get(reportKey) ?? EMPTY_DERIVED_METRICS}
                      compareEnabled={compareEnabled}
                      isLoading={campaignsLoading}
                      activeDragColumnId={campaignColumns.activeDragColumnId}
                      hoverColumnId={campaignColumns.hoverColumnId}
                      hoverPosition={campaignColumns.hoverPosition}
                      renderEntityCell={(columnId, meta) => {
                        if (columnId === 'name') {
                          return (
                            <td
                              style={meta.style}
                              className={`${workspaceTableClass.td} ${meta.className} font-medium text-foreground`}
                            >
                              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                <Link
                                  to={detailsPath}
                                  onClick={(event) => openDetails(event, detailsPath)}
                                  className="min-w-0 truncate text-brand hover:underline"
                                  title={row.name}
                                >
                                  {row.name}
                                </Link>
                                <CampaignPerformanceGraphButton
                                  campaignName={row.name}
                                  onClick={() =>
                                    setPerformanceDialogCampaign({
                                      campaignId: row.campaignId,
                                      campaignName: row.name,
                                    })
                                  }
                                />
                              </div>
                            </td>
                          );
                        }
                        if (columnId === 'adProduct') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <Badge variant="neutral">{row.adProduct}</Badge>
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'state') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <CampaignStateBadge state={row.state} />
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'budget') {
                          const editableTarget = campaignNode?.raw
                            ? resolveEditableCampaignBudget(campaignNode.raw)
                            : null;
                          return (
                            <td
                              style={meta.style}
                              className={`${workspaceTableClass.td} ${meta.className} text-right font-mono tabular-nums text-[11px]`}
                            >
                              <EditableCampaignBudgetCell
                                profileId={profileId}
                                displayValue={formatCurrencyValue(
                                  editableTarget?.budget ??
                                    (typeof campaignNode?.raw.budget === 'number'
                                      ? campaignNode.raw.budget
                                      : null)
                                )}
                                editableTarget={editableTarget}
                                campaignLabel={row.name}
                                onUpdated={() => {
                                  onCatalogRefresh();
                                  invalidateCampaigns();
                                }}
                              />
                            </td>
                          );
                        }
                        if (columnId === 'autoBidding') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <AutoBiddingColumnCell
                                enabled={row.autoBiddingEnabled === true}
                                adProduct={row.adProduct}
                                settings={row.autoBidding}
                                disabled={campaignsLoading}
                                onOpen={() => {
                                  if (!supportsAutoBidding(row.adProduct)) return;
                                  setAutoBiddingCampaign({
                                    campaignId: row.campaignId,
                                    campaignName: row.name,
                                    adProduct: row.adProduct,
                                  });
                                }}
                              />
                            </WorkspaceMiddleCell>
                          );
                        }
                        if (columnId === 'outOfBudgetBoost') {
                          return (
                            <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                              <OutOfBudgetBoostSwitch
                                enabled={row.outOfBudgetBoostEnabled === true}
                                disabled={campaignsLoading}
                                onClick={() =>
                                  setOutOfBudgetBoostCampaign({
                                    scope: 'campaign',
                                    campaignId: row.campaignId,
                                    name: row.name,
                                    adProduct: row.adProduct,
                                  })
                                }
                              />
                            </WorkspaceMiddleCell>
                          );
                        }
                        return null;
                      }}
                    />
                    {showPlacementColumns ? (
                      <>
                        <WorkspaceCampaignPlacementBidsCell raw={campaignNode?.raw.raw} />
                        <WorkspaceCampaignPlacementMetricCells
                          byPlacement={placementByCampaign.get(reportKey) ?? null}
                          previousByPlacement={previousPlacementByCampaign.get(reportKey) ?? null}
                          compareEnabled={compareEnabled}
                          isLoading={deferredLoading}
                        />
                      </>
                    ) : null}
                    <td className={workspaceTableClass.td}>
                      <Link
                        to={detailsPath}
                        onClick={(event) => openDetails(event, detailsPath)}
                        className="text-sm font-medium text-brand hover:underline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {campaignsPage.rows.length > 0 ? (
              <tfoot>
                <WorkspaceOrderedTotalsRow
                  order={campaignColumns.displayOrder}
                  frozenColumnIds={campaignColumns.frozenColumnIds}
                  frozenLeftById={campaignFreeze.frozenLeftById}
                  current={campaignCurrentTotals}
                  previous={campaignPreviousTotals}
                  compareEnabled={compareEnabled}
                  isLoading={campaignsLoading}
                  trailingColSpan={showPlacementColumns ? 5 : 1}
                />
              </tfoot>
            ) : null}
          </table>
        </WorkspaceTableShell>
      </WorkspaceSection>

      {autoBiddingCampaign ? (
        <WorkspaceAutoBiddingModal
          profileId={profileId}
          campaignId={autoBiddingCampaign.campaignId}
          campaignName={autoBiddingCampaign.campaignName}
          adProduct={autoBiddingCampaign.adProduct}
          onClose={() => setAutoBiddingCampaign(null)}
          onChanged={() => {
            showToast('Auto bidding updated', 'success');
            invalidateCampaigns();
          }}
        />
      ) : null}

      {outOfBudgetBoostCampaign ? (
        <WorkspaceOutOfBudgetBoostModal
          profileId={profileId}
          target={outOfBudgetBoostCampaign}
          onClose={() => setOutOfBudgetBoostCampaign(null)}
          onChanged={() => {
            showToast('Out of budget settings updated', 'success');
            invalidateCampaigns();
          }}
        />
      ) : null}

      {campaignBudgetModalOpen ? (
        <WorkspaceCampaignBudgetModal
          pending={campaignBudgetApplying}
          onCancel={() => setCampaignBudgetModalOpen(false)}
          onConfirm={handleOpenCampaignBudgetPreview}
        />
      ) : null}

      {campaignBudgetPreviewRows ? (
        <BulkCampaignBudgetConfirmModal
          rows={campaignBudgetPreviewRows}
          pending={campaignBudgetApplying}
          formatCurrency={(amount) => formatCurrencyValue(amount)}
          onConfirm={() => void handleConfirmCampaignBudget()}
          onCancel={() => {
            if (!campaignBudgetApplying) setCampaignBudgetPreviewRows(null);
          }}
        />
      ) : null}

      {campaignStatusModalOpen ? (
        <WorkspaceStateChangeModal
          title="Change Campaign Status"
          entityLabel="campaign"
          selectionCount={selectedCampaigns.length}
          pending={campaignStatusApplying}
          onCancel={() => setCampaignStatusModalOpen(false)}
          onConfirm={(state) => void handleConfirmCampaignState(state)}
        />
      ) : null}

      {campaignAudienceModalOpen ? (
        <WorkspaceCampaignAudienceModal
          profileId={profileId}
          eligibleCount={selectedAudienceCampaigns.eligible.length}
          skippedSdCount={selectedAudienceCampaigns.skippedSd.length}
          pending={campaignAudienceApplying}
          onCancel={() => {
            if (!campaignAudienceApplying) setCampaignAudienceModalOpen(false);
          }}
          onConfirm={(audienceId, percentage) =>
            void handleConfirmCampaignAudience(audienceId, percentage)
          }
        />
      ) : null}

      {campaignPlacementModalOpen ? (
        <WorkspaceCampaignPlacementModal
          selectionCount={selectedCampaigns.length}
          pending={campaignPlacementJob.isRunning}
          onCancel={() => setCampaignPlacementModalOpen(false)}
          onConfirm={(adjustments) => void handleConfirmCampaignPlacement(adjustments)}
        />
      ) : null}

      {campaignPlacementJob.job ? (
        <WorkspaceCampaignPlacementProgressModal
          job={campaignPlacementJob.job}
          failedResults={campaignPlacementJob.failedResults}
          isRunning={campaignPlacementJob.isRunning}
          onClose={() => campaignPlacementJob.reset()}
          onRetryFailed={() => {
            if (!campaignPlacementJob.job) return;
            void campaignPlacementJob
              .retryFailed(campaignPlacementJob.job.id)
              .then((final) => {
                const failed = final.totals.failed ?? 0;
                const applied = final.totals.applied ?? 0;
                if (failed > 0) {
                  showToast(`Retry finished: ${applied} applied, ${failed} still failed`, 'error');
                } else {
                  showToast(
                    `Retry applied ${applied} campaign${applied === 1 ? '' : 's'}`,
                    'success'
                  );
                }
                onRefresh();
              })
              .catch((error) => {
                showToast(
                  error instanceof Error ? error.message : 'Failed to retry placement bidding',
                  'error'
                );
              });
          }}
        />
      ) : null}

      <CampaignPerformanceDialog
        isOpen={performanceDialogCampaign !== null}
        onClose={() => setPerformanceDialogCampaign(null)}
        profileId={profileId}
        campaignId={performanceDialogCampaign?.campaignId ?? ''}
        campaignName={performanceDialogCampaign?.campaignName ?? ''}
      />

      {demoModals}
    </>
  );
}
